import dialogPolyfill from 'dialog-polyfill';
import { disableBodyScroll, enableBodyScroll } from './body-scroll-lock';

/**
 * Open and close dialog element.
 *
 * @class    Dialog
 * @param    {HTMLDialogElement} dialog      - Dialog element.
 * @param    {HTMLButtonElement} openButton  - Button element for opening the dialog.
 * @property {HTMLDialogElement} dialog      - Dialog element.
 * @property {HTMLButtonElement} openButton  - Button element for opening the dialog.
 * @property {HTMLButtonElement} closeButton - Button element for closing the dialog.
 * @property {HTMLFormElement}   form        - Form element possibly contained by the dialog.
 */

class Dialog {
  constructor(dialog, openButton) {
    this.dialog = dialog;
    this.openButton = openButton;
    this.closeButton = dialog.getElementsByClassName('dialog__close-button')[0];
    this.form = dialog.getElementsByClassName('ajax-form')[0];

    if (this.dialog.open) {
      disableBodyScroll(this.dialog);
      this.dialog.style.maxHeight = `${window.innerHeight}px`;
    }

    if ('closeOnBackdrop' in this.dialog.dataset) {
      this.dialog.addEventListener('mouseup', event => this.onDialogMouseEvent(event));
      this.dialog.addEventListener('mousedown', event => this.onDialogMouseEvent(event));
    }

    this.dialog.addEventListener('close', () => this.onDialogClose());
    this.openButton?.addEventListener('click', () => this.onOpenButtonClick());
    this.closeButton?.addEventListener('click', () => this.dialog.close());
    this.form?.addEventListener('submit-success', () => this.dialog.close());
  }

  /**
   * Close dialog on backdrop click.
   *
   * @param  {Object} event - Click event.
   *
   * @return {undefined}
   */

  onDialogMouseEvent({ clientX: x, clientY: y, type, target }) {
    const { left, right, top, bottom } = this.dialog.getBoundingClientRect();

    // Check if mouse event is outside of dialog, thus on backdrop
    if (x < left || x > right || y < top || y > bottom) {
      if (type === 'mousedown') {
        this.backdropMousedown = true; // Remember mousedown on backdrop
      }
      // Check if mouseup on backdrop and(!) previous mousedown was on backdrop as well
      else if (type === 'mouseup' && this.backdropMousedown && target === this.dialog) {
        this.dialog.close();
        this.backdropMousedown = false;
      }
    }
    else {
      this.backdropMousedown = false;
    }
  }

  /**
   * Enable body scroll and focus on dialog open button on dialog close.
   *
   * @return {undefined}
   */

  onDialogClose() {
    enableBodyScroll(this.dialog);
    this.openButton.focus();
  }

  /**
   * Open dialog modal on button click.
   *
   * @return {undefined}
   */

  onOpenButtonClick() {
    disableBodyScroll(this.dialog);
    this.dialog.style.maxHeight = `${window.innerHeight}px`;
    this.dialog.showModal();
  }
}

/**
 * Initialize and get instances of Dialog.
 *
 * @param  {HTMLCollection} dialogs - Collection of dialog elements.
 *
 * @return {Dialog[]}       Instances of Dialog.
 */

function initDialogs(dialogs) {
  const buttons = Array.from(document.getElementsByClassName('dialog__open-button'));

  // If dialog element not supported
  if (!window.HTMLDialogElement) {
    // Move dialogs to the end of body to not get tripped on the stacking context
    document.body.append(...dialogs);
  }

  return Array.from(dialogs).map(dialog => {
    // Polyfill dialog element
    dialogPolyfill.registerDialog(dialog);

    // Find button to open the dialog
    const button = buttons.find(({ dataset: { dialogIndex } }) => (
      dialogIndex === dialog.dataset.dialogIndex
    ));

    return new Dialog(dialog, button);
  });
}

export default initDialogs;
